import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiError } from '../../../abstract/api-error';
import { EpaChemicalsService } from '../../../core/data-services/epa-chemicals.service';
import { WebSocketService } from '../../../web-socket-handlers/web-socket.service';
import { EpaChemical } from '../../../core/data-models/epa-chemical.model';
import { ChemicalCategory } from '../../../core/data-models/chemical-category.model';
import { SignalWord } from '../../../core/data-models/signal-word.model';
import { EpaChemicalStatus } from '../../../core/data-models/epa-chemical-status.model';
import { EpaChemicalFilters } from '../../../core/request-models/epa-chemical-filters.model';
import { EpaSyncStatus } from '../../../core/response-models/epa-sync-status.model';

/**
 * EPA Chemicals List Component
 *
 * Displays paginated list of EPA-synced chemicals with filtering,
 * search, and navigation to detail view. Shows sync status and
 * allows triggering manual syncs.
 */
@Component({
  selector: 'app-epa-chemicals-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './epa-chemicals-list.component.html',
  styleUrl: './epa-chemicals-list.component.scss'
})
export class EpaChemicalsListComponent implements OnInit, OnDestroy {
  chemicals: EpaChemical[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Sync status
  syncStatus: EpaSyncStatus | null = null;
  syncing = false;
  backfilling = false;
  cancelling = false;
  currentJobId: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalChemicals = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterCategory: ChemicalCategory | 'all' = 'all';
  filterSignalWord: SignalWord | 'all' = 'all';
  filterStatus: EpaChemicalStatus | 'all' = 'all';
  filterRupFlag: 'all' | 'yes' | 'no' = 'all';
  filterIsHidden: 'all' | 'visible' | 'hidden' = 'all';

  // Category options
  categoryOptions: { value: ChemicalCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'All Categories' },
    { value: 'herbicide', label: 'Herbicide' },
    { value: 'insecticide', label: 'Insecticide' },
    { value: 'fungicide', label: 'Fungicide' },
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'adjuvant', label: 'Adjuvant' },
    { value: 'other', label: 'Other' }
  ];

  // Signal word options
  signalWordOptions: { value: SignalWord | 'all'; label: string }[] = [
    { value: 'all', label: 'All Signal Words' },
    { value: 'CAUTION', label: 'Caution' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'DANGER', label: 'Danger' }
  ];

  // Status options
  statusOptions: { value: EpaChemicalStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Active', label: 'Active' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Pending', label: 'Pending' },
    { value: 'removed-from-epa', label: 'Removed from EPA' }
  ];

  // RUP options
  rupOptions: { value: 'all' | 'yes' | 'no'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'yes', label: 'Restricted Use Only' },
    { value: 'no', label: 'Non-Restricted Only' }
  ];

  // Hidden options
  hiddenOptions: { value: 'all' | 'visible' | 'hidden'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'visible', label: 'Visible Only' },
    { value: 'hidden', label: 'Hidden Only' }
  ];

  // WebSocket event handlers (stored for cleanup)
  private onProgress = (event: any) => this.handleSyncProgress(event);
  private onComplete = (event: any) => this.handleSyncComplete(event);
  private onFailed = (event: any) => this.handleSyncFailed(event);

  constructor(
    private router: Router,
    private epaChemicalsService: EpaChemicalsService,
    private webSocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    this.loadChemicals();
    this.loadSyncStatus();
    this.registerWebSocketHandlers();
  }

  ngOnDestroy(): void {
    this.webSocketService.unregisterEventHandler('epa-sync:progress', this.onProgress);
    this.webSocketService.unregisterEventHandler('epa-sync:complete', this.onComplete);
    this.webSocketService.unregisterEventHandler('epa-sync:failed', this.onFailed);
  }

  /**
   * Load chemicals with current filters
   */
  loadChemicals(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters: EpaChemicalFilters = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.searchQuery.trim()) {
      filters.query = this.searchQuery.trim();
    }

    if (this.filterCategory !== 'all') {
      filters.category = this.filterCategory;
    }

    if (this.filterSignalWord !== 'all') {
      filters.signalWord = this.filterSignalWord;
    }

    if (this.filterStatus !== 'all') {
      filters.status = this.filterStatus;
    }

    if (this.filterRupFlag === 'yes') {
      filters.rupFlag = true;
    } else if (this.filterRupFlag === 'no') {
      filters.rupFlag = false;
    }

    if (this.filterIsHidden === 'visible') {
      filters.isHidden = false;
    } else if (this.filterIsHidden === 'hidden') {
      filters.isHidden = true;
    }

    this.epaChemicalsService.search(filters).subscribe({
      next: (response) => {
        this.chemicals = response.items;
        this.totalChemicals = response.paging.totalItems;
        this.totalPages = response.paging.totalPages;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load EPA chemicals:', error);
        this.errorMessage = 'Failed to load EPA chemicals';
        this.loading = false;
      }
    });
  }

  /**
   * Load sync status from REST API (initial load only)
   */
  loadSyncStatus(): void {
    this.epaChemicalsService.getSyncStatus().subscribe({
      next: (status) => {
        this.syncStatus = status;
        if (status.currentJobId) {
          this.currentJobId = status.currentJobId;
        }
      },
      error: (error) => {
        console.error('Failed to load sync status:', error);
      }
    });
  }

  /**
   * Register WebSocket handlers for real-time sync progress
   */
  private registerWebSocketHandlers(): void {
    this.webSocketService.registerEventHandler('epa-sync:progress', this.onProgress);
    this.webSocketService.registerEventHandler('epa-sync:complete', this.onComplete);
    this.webSocketService.registerEventHandler('epa-sync:failed', this.onFailed);
  }

  private handleSyncProgress(event: { jobId: string; progress: number; status: string; jobType: string }): void {
    if (this.syncStatus) {
      this.syncStatus = {
        ...this.syncStatus,
        isRunning: true,
        progress: event.progress,
        currentJobId: event.jobId,
      };
    }
  }

  private handleSyncComplete(event: { jobId: string; status: string; statistics?: any; jobType: string }): void {
    this.syncing = false;
    this.backfilling = false;
    this.currentJobId = null;
    this.successMessage = 'Sync completed successfully';
    this.clearSuccessMessage();
    // Reload both sync status and chemicals list
    this.loadSyncStatus();
    this.loadChemicals();
  }

  private handleSyncFailed(event: { jobId: string; status: string; error: string; jobType: string }): void {
    this.syncing = false;
    this.backfilling = false;
    this.currentJobId = null;
    this.errorMessage = event.error || 'Sync job failed';
    this.loadSyncStatus();
  }

  /**
   * Trigger manual sync
   */
  triggerSync(): void {
    if (this.syncing || this.syncStatus?.isRunning) {
      return;
    }

    this.syncing = true;
    this.errorMessage = '';

    this.epaChemicalsService.triggerSync().subscribe({
      next: (response) => {
        this.currentJobId = response.jobId;
        this.successMessage = response.message || 'Sync job triggered successfully';
        this.syncing = false;
        if (this.syncStatus) {
          this.syncStatus = { ...this.syncStatus, isRunning: true, progress: 0, currentJobId: response.jobId };
        }
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to trigger sync:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to trigger sync';
        this.syncing = false;
      }
    });
  }

  /**
   * Trigger PPLS backfill
   */
  triggerPplsBackfill(): void {
    if (this.backfilling || this.syncStatus?.isRunning) {
      return;
    }

    this.backfilling = true;
    this.errorMessage = '';

    this.epaChemicalsService.triggerPplsBackfill().subscribe({
      next: (response) => {
        this.currentJobId = response.jobId;
        this.successMessage = response.message || 'PPLS backfill triggered successfully';
        this.backfilling = false;
        if (this.syncStatus) {
          this.syncStatus = { ...this.syncStatus, isRunning: true, progress: 0, currentJobId: response.jobId };
        }
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to trigger PPLS backfill:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to trigger PPLS backfill';
        this.backfilling = false;
      }
    });
  }

  /**
   * Cancel/force-remove the current sync job
   */
  cancelJob(): void {
    if (this.cancelling) return;

    this.cancelling = true;
    this.errorMessage = '';

    this.epaChemicalsService.cancelSyncJob(this.currentJobId || undefined).subscribe({
      next: () => {
        this.cancelling = false;
        this.syncing = false;
        this.backfilling = false;
        this.currentJobId = null;
        // Locally reset UI immediately — don't wait for loadSyncStatus()
        // which may still see the job as "active" before the worker reaches a checkpoint
        if (this.syncStatus) {
          this.syncStatus = { ...this.syncStatus, isRunning: false, progress: null, currentJobId: null };
        }
        this.successMessage = 'Job cancelled successfully';
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to cancel job:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to cancel job';
        this.cancelling = false;
      }
    });
  }

  /**
   * Handle search submission
   */
  onSearch(): void {
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.filterCategory = 'all';
    this.filterSignalWord = 'all';
    this.filterStatus = 'all';
    this.filterRupFlag = 'all';
    this.filterIsHidden = 'all';
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Check if any filters are active
   */
  hasActiveFilters(): boolean {
    return (
      this.searchQuery !== '' ||
      this.filterCategory !== 'all' ||
      this.filterSignalWord !== 'all' ||
      this.filterStatus !== 'all' ||
      this.filterRupFlag !== 'all' ||
      this.filterIsHidden !== 'all'
    );
  }

  /**
   * Navigate to page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadChemicals();
    }
  }

  /**
   * Get pagination range for display
   */
  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Navigate to chemical detail view
   */
  viewChemical(chemical: EpaChemical): void {
    this.router.navigate(['/epa-chemicals', chemical.id]);
  }

  /**
   * Get category display label
   */
  getCategoryLabel(category: string | null | undefined): string {
    if (!category) return '-';
    const option = this.categoryOptions.find(opt => opt.value === category);
    return option?.label || category;
  }

  /**
   * Get signal word badge class
   */
  getSignalWordClass(signalWord: string | null | undefined): string {
    switch (signalWord?.toUpperCase()) {
      case 'DANGER':
        return 'bg-danger';
      case 'WARNING':
        return 'bg-warning text-dark';
      case 'CAUTION':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'Active':
        return 'bg-success';
      case 'Cancelled':
        return 'bg-danger';
      case 'Pending':
        return 'bg-warning text-dark';
      case 'removed-from-epa':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format sync date for display
   */
  formatSyncDate(dateString: string | null | undefined): string {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get display name for chemical (primary custom name or EPA name)
   */
  getDisplayName(chemical: EpaChemical): string {
    return chemical.displayName || chemical.name || '';
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  readonly Math = Math;
}
